import * as React from 'react';

import { fromJson } from '@bufbuild/protobuf';
import {
  ChatEvent,
  ChatEventSchema,
} from '@rematter/frontend-lib-api-client/grpc/assistant/v1/events_pb';
import { Col, EventDetailContainer, JsonViewer } from '@rematter/ui-web';

import { useTranslations } from '../../../i18n/useTranslations';

import { ChatArchivedDetail } from './ChatArchivedDetail';
import { ChatCreatedDetail } from './ChatCreatedDetail';
import { ChatTitleUpdatedDetail } from './ChatTitleUpdatedDetail';
import { ChatUpdatedDetail } from './ChatUpdatedDetail';

type ChatEventDetailProps = {
  payload: string;
};

//
// ⚛️ ⚛️ ⚛️ CHAT EVENT DETAIL ⚛️ ⚛️ ⚛️
//
export const ChatEventDetail = ({ payload }: ChatEventDetailProps) => {
  const t = useTranslations();

  // States
  const [showJson, setShowJson] = React.useState(false);

  // Memoized values
  const event = React.useMemo((): ChatEvent | null => {
    try {
      return fromJson(ChatEventSchema, JSON.parse(payload));
    } catch {
      return null;
    }
  }, [payload]);

  if (!event) {
    return <JsonViewer data={payload} title={t.logs.rawPayloadParseError} />;
  }

  const { event: inner } = event;

  const titleByEventCase: Record<NonNullable<typeof inner.case>, string> = {
    created: t.logs.chatCreated,
    updated: t.logs.chatUpdated,
    archived: t.logs.chatArchived,
    titleUpdated: t.logs.chatTitleUpdated,
  };

  return (
    <Col className="@ChatEventDetail flex-1">
      <EventDetailContainer
        title={inner.case ? titleByEventCase[inner.case] : t.logs.unknownEvent}
        showJson={showJson}
        onToggleJson={() => setShowJson(!showJson)}
        jsonContent={payload}
      >
        <ChatEventContent inner={inner} payload={payload} />
      </EventDetailContainer>
    </Col>
  );
};

type ChatEventContentProps = {
  inner: ChatEvent['event'];
  payload: string;
};

//
// ⚛️ ⚛️ ⚛️ CHAT EVENT CONTENT ⚛️ ⚛️ ⚛️
//
const ChatEventContent = ({ inner, payload }: ChatEventContentProps) => {
  const t = useTranslations();

  switch (inner.case) {
    case 'created':
      return <ChatCreatedDetail event={inner.value} />;
    case 'updated':
      return <ChatUpdatedDetail event={inner.value} />;
    case 'archived':
      return <ChatArchivedDetail event={inner.value} />;
    case 'titleUpdated':
      return <ChatTitleUpdatedDetail event={inner.value} />;
    case undefined:
      return <JsonViewer data={payload} title={t.logs.unknownEvent} />;
  }
};
